/**
 * @description: 火山引擎埋点
 */
import store from '@/store'

// 声明全局类型
declare global {
  interface Window {
    collectEvent: (eventName: string, eventData?: Record<string, any>) => void
    dataFinderAppId: number
    VUE_APP_AREA?: string
  }
}

const isDevelopment = process.env.NODE_ENV === 'development'

interface DataFinderConfig {
  app_id: number
  channel_domain: string
  disable_sdk_monitor: boolean
  debug: boolean
  log: boolean
  enable_stay_duration: boolean
  spa: boolean
  enable_debug: boolean
}

const getDataFinderConfig = (appId: number): DataFinderConfig | undefined => {
  if (!appId) return
  return {
    app_id: appId,
    channel_domain: 'https://gator.uba.ap-southeast-1.volces.com',
    disable_sdk_monitor: true, // 用于禁止SDK启动后自身监控事件 onload 的上报
    debug: isDevelopment,
    log: isDevelopment, // 本地调试日志打印
    enable_stay_duration: true, // true：开启停留时长
    spa: true,
    enable_debug: isDevelopment // 开启devTool ?open_devtool_web=true&app_id={xxxxxx}
  }
}

class DataFinder {
  private appId?: number
  private isInitialized = false

  constructor() {
    this.initConfig()
  }

  /**
   * 初始化配置（仅配置，不涉及用户信息）
   */
  private initConfig(): void {
    if (this.isInitialized) return

    try {
      this.appId = window.dataFinderAppId
      if (!this.appId) {
        console.warn('DataFinder: appId 未配置')
        return
      }

      const config = getDataFinderConfig(this.appId)
      if (config) {
        window.collectEvent('init', config)
        this.isInitialized = true
        console.log('DataFinder 初始化完成, appId:', this.appId)
      }
    } catch (error) {
      console.error('火山引擎埋点初始化错误:', error)
    }
  }

  /**
   * 启动埋点（包含用户信息配置和开始上报）
   */
  init(): void {
    if (!this.isInitialized) {
      console.warn('DataFinder: 配置未初始化，无法启动')
      return
    }

    try {
      // 使用当前登录用户的信息做为唯一标识来进行上报
      const userInfo = store.state.user?.userInfo
      const userId = userInfo?.uid?.toString()
      if (userId) {
        const area = window.VUE_APP_AREA || 'default'
        const userIdstr = `${area}-des-${userId}`
        window.collectEvent('config', {
          user_unique_id: userIdstr,
          user_id: userId,
          device_platform: 'web'
        })

        const selectedGroup = store.getters['microAppCache/getSelectedGroup']
        window.collectEvent('profileSet', {
          user_name: userInfo?.name || '',
          group_id: selectedGroup?.groupId ?? '',
          group_name: selectedGroup?.groupName ?? '',
          group_type: selectedGroup?.type ?? '', // 1 中心 2站点
          group_country: selectedGroup?.country ?? ''
        })
      }

      // 开始事件上报
      window.collectEvent('start')
    } catch (error) {
      console.error('事件监听初始化错误:', error)
    }
  }

  /**
   * 上报自定义用户事件
   */
  collectEvent(eventName: string, eventData?: Record<string, any>): void {
    if (!this.isInitialized) {
      console.warn('DataFinder: 未初始化，无法上报事件')
      return
    }

    try {
      window.collectEvent(eventName, {
        ...eventData,
        page_url: window.location.href,
        page_title: document.title
      })
    } catch (error) {
      console.error('火山引擎埋点事件监听错误:', error)
    }
  }
}

export const dataFinder = new DataFinder()
